import {
  forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from "react";
import { ChevronDown } from "lucide-react";
import { cx } from "../../lib/cx.js";

const CONTROL =
  "w-full rounded-md border border-border-strong bg-surface text-fg placeholder:text-subtle " +
  "transition-colors hover:border-subtle focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:border-accent " +
  "disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-subtle " +
  "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:outline-danger";

const CONTROL_SIZES = {
  sm: "h-8 px-2.5 text-[13px]",
  md: "h-9 px-3 text-sm",
  lg: "h-12 px-4 text-base",
} as const;

export interface ControlProps {
  id: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
}

interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  /** Oculta la etiqueta visualmente (sigue disponible para lectores de pantalla). */
  hideLabel?: boolean;
  className?: string;
  children: (p: ControlProps) => ReactNode;
}

/** Etiqueta + control + ayuda + error, cableados con aria-describedby / aria-invalid. */
export function Field({ label, hint, error, required, hideLabel, className, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const describedBy = [errId, hintId].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("flex min-w-0 flex-col gap-1", className)}>
      <label htmlFor={id} className={cx("text-[13px] font-medium text-fg", hideLabel && "sr-only")}>
        {label}
        {required && <span className="ml-0.5 text-danger" aria-hidden="true">*</span>}
      </label>
      {children({
        id,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": describedBy,
        "aria-required": required || undefined,
      })}
      {error && (
        <p id={errId} className="text-[12.5px] font-medium text-danger">
          {error}
        </p>
      )}
      {hint && !error && (
        <p id={hintId} className="text-[12.5px] text-subtle">
          {hint}
        </p>
      )}
    </div>
  );
}

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  size?: keyof typeof CONTROL_SIZES;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ size = "md", className, ...rest }, ref) {
  return <input ref={ref} className={cx(CONTROL, CONTROL_SIZES[size], className)} {...rest} />;
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea(
  { className, ...rest },
  ref,
) {
  return <textarea ref={ref} className={cx(CONTROL, "min-h-20 px-3 py-2 text-sm", className)} {...rest} />;
});

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  size?: keyof typeof CONTROL_SIZES;
}

/** <select> nativo (mejor accesibilidad y teclado en móvil) con estilo propio. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = "md", className, children, ...rest },
  ref,
) {
  return (
    <div className={cx("relative min-w-0", className)}>
      <select ref={ref} className={cx(CONTROL, CONTROL_SIZES[size], "appearance-none pr-8")} {...rest}>
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute top-1/2 right-2.5 size-4 -translate-y-1/2 text-subtle"
        aria-hidden="true"
      />
    </div>
  );
});

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({ label, description, className, id, ...rest }: CheckboxProps) {
  const autoId = useId();
  const cid = id ?? autoId;
  return (
    <div className={cx("flex items-start gap-2", className)}>
      <input
        id={cid}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 rounded border-border-strong accent-[var(--accent)]"
        aria-describedby={description ? `${cid}-desc` : undefined}
        {...rest}
      />
      <div className="min-w-0">
        <label htmlFor={cid} className="cursor-pointer text-sm text-fg">
          {label}
        </label>
        {description && (
          <p id={`${cid}-desc`} className="text-[12.5px] text-subtle">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
}

/** Interruptor accesible (role="switch"), para opciones de encendido/apagado. */
export function Switch({ checked, onChange, label, description, disabled, className }: SwitchProps) {
  const id = useId();
  return (
    <div className={cx("flex items-start gap-2.5", className)}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={description ? `${id}-desc` : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cx(
          "relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full border transition-colors",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked ? "border-accent bg-accent" : "border-border-strong bg-surface-3",
        )}
      >
        <span
          aria-hidden="true"
          className={cx(
            "inline-block size-3.5 rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-[2px]",
          )}
        />
      </button>
      <div className="min-w-0">
        <label htmlFor={id} className={cx("text-sm text-fg", disabled ? "cursor-not-allowed" : "cursor-pointer")}>
          {label}
        </label>
        {description && (
          <p id={`${id}-desc`} className="text-[12.5px] text-subtle">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
